import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { LIST_SITUATION_TEMPLATES, GET_MATERIAL, LIST_SCENARIOS } from '../../../graphql/queries';
import { UPDATE_MATERIAL } from '../../../graphql/mutations';
import { Scenario, Material, SituationTemplate } from '../../../types/models';
import { uploadService } from '../../../services/uploadService';
import {
  PDF_VALIDATIONS,
  SCENARIO_VALIDATIONS,
  validatePdfFiles,
  extractTextFromPdf,
  validatePdfTextLength,
  validateScenario
} from '../../../utils/validationUtils';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Chip,
  Autocomplete,
  FormControlLabel,
  Switch,
  CircularProgress,
  RadioGroup,
  Radio,
  Alert,
  useTheme,
  Menu,
  MenuItem
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

const genderOptions = ['男性', '女性'];
const ageOptions = ['20～30代', '40～50代'];
const languageOptions = ['日本語', '英語'];

export interface ScenarioFormData {
  title: string;
  description: string;
  goal: string;
  estimatedTime: number;
  situation: string;
  targetName: string;
  targetGender: string;
  targetAge: string;
  targetLanguage?: string;
  tags: string;
  isActive: boolean;
  department?: string;
  showMaterialsOnOrientation: boolean;
  situationClosed: string;
}

export interface ScenarioFormPayload {
  formData: ScenarioFormData;
  uploadedFiles: File[];
  materialsToDelete: string[];
  isActive: boolean;
}

const initialFormData: ScenarioFormData = {
  title: '',
  description: '',
  goal: '',
  estimatedTime: 30,
  situation: '',
  targetName: '顧客',
  targetGender: '男性',
  targetAge: '20～30代',
  targetLanguage: '日本語',
  tags: '',
  isActive: true,
  department: '',
  showMaterialsOnOrientation: true,
  situationClosed: ''
};

interface ScenarioFormDialogProps {
  open: boolean;
  initialScenario: Scenario | null;
  companyId: string;
  displayLimit: number;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: ScenarioFormPayload) => Promise<void>;
}

const ScenarioFormDialog: React.FC<ScenarioFormDialogProps> = ({
  open,
  initialScenario,
  companyId,
  displayLimit,
  saving,
  onClose,
  onSave
}) => {
  const theme = useTheme();
  const { currentUser } = useSelector((state: RootState) => state.user);

  const [formData, setFormData] = useState<ScenarioFormData>(initialFormData);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [materialsToDelete, setMaterialsToDelete] = useState<string[]>([]);
  const [dialogErrorMessage, setDialogErrorMessage] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    material: Material | null;
  } | null>(null);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [openTextDialog, setOpenTextDialog] = useState(false);
  const [currentMaterial, setCurrentMaterial] = useState<Material | null>(null);
  const [extractedText, setExtractedText] = useState<string>('');
  const [textDialogLoading, setTextDialogLoading] = useState(false);
  const [textDialogError, setTextDialogError] = useState<string | null>(null);

  const { loading: templateLoading, data: templateData, refetch: templateRefetch } = useQuery(
    LIST_SITUATION_TEMPLATES,
    { variables: { limit: 100 }, fetchPolicy: 'cache-and-network', skip: !open }
  );
  const [getMaterial] = useLazyQuery(GET_MATERIAL);
  const [updateMaterial] = useMutation(UPDATE_MATERIAL, {
    refetchQueries: [{ query: LIST_SCENARIOS, variables: { companyId, limit: displayLimit } }]
  });

  useEffect(() => {
    if (!open) return;
    if (initialScenario) {
      setFormData({
        title: initialScenario.title,
        description: initialScenario.description,
        goal: initialScenario.goal,
        estimatedTime: initialScenario.estimatedTime || 15,
        situation: initialScenario.situation || '',
        targetName: initialScenario.targetName || '',
        targetGender: initialScenario.targetGender || '男性',
        targetAge: initialScenario.targetAge || '20～30代',
        targetLanguage: initialScenario.targetLanguage || languageOptions[0],
        tags: initialScenario.tags?.join(', ') || '',
        isActive: initialScenario.isActive,
        department: initialScenario.department || '',
        showMaterialsOnOrientation: initialScenario.showMaterialsOnOrientation ?? true,
        situationClosed: initialScenario.situationClosed ?? ''
      });
    } else {
      setFormData({
        ...initialFormData,
        department: currentUser?.managementScope?.trim() || currentUser?.department || '',
        estimatedTime: 15
      });
    }
    setUploadedFiles([]);
    setFileNames([]);
    setFileError(null);
    setMaterialsToDelete([]);
    setDialogErrorMessage(null);
    setLoadError(null);
    setContextMenu(null);
    setOpenTemplateDialog(false);
    setSelectedTemplateId('');
  }, [open, initialScenario, currentUser?.department]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'estimatedTime' ? parseInt(value, 10) || 0 : value
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const isSaveDisabled = () => {
    const validationResult = validateScenario({
      title: formData.title,
      description: formData.description,
      goal: formData.goal,
      estimatedTime: formData.estimatedTime,
      situation: formData.situation,
      situationClosed: formData.situationClosed,
      targetName: formData.targetName
    });
    return !validationResult.isValid;
  };

  const handleSave = async (isActive: boolean) => {
    const validationResult = validateScenario({
      title: formData.title,
      description: formData.description,
      goal: formData.goal,
      estimatedTime: formData.estimatedTime,
      situation: formData.situation,
      situationClosed: formData.situationClosed,
      targetName: formData.targetName,
      department: formData.department
    });
    if (!validationResult.isValid) {
      setDialogErrorMessage(validationResult.errors.map(e => e.message).join('\n'));
      return;
    }
    setDialogErrorMessage(null);
    try {
      await onSave({
        formData,
        uploadedFiles,
        materialsToDelete,
        isActive
      });
    } catch (err: unknown) {
      setDialogErrorMessage(err instanceof Error ? err.message : 'ロープレの保存中にエラーが発生しました');
    }
  };

  const handleUseTemplate = () => {
    templateRefetch();
    setOpenTemplateDialog(true);
  };

  const handleCloseTemplateDialog = () => {
    setOpenTemplateDialog(false);
    setSelectedTemplateId('');
  };

  const handleApplyTemplate = () => {
    if (!selectedTemplateId || !templateData?.listSituationTemplate?.items) return;
    const selected = templateData.listSituationTemplate.items.find(
      (t: SituationTemplate) => t.templateId === selectedTemplateId
    );
    if (selected) {
      setFormData(prev => ({ ...prev, situation: selected.body }));
    }
    handleCloseTemplateDialog();
  };

  const handleLoadScenario = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        setLoadError(null);
        const arrayBuffer = await file.arrayBuffer();
        let text: string;
        let data: Record<string, unknown>;
        try {
          text = new TextDecoder('utf-8').decode(arrayBuffer);
          data = JSON.parse(text) as Record<string, unknown>;
        } catch {
          try {
            text = new TextDecoder('shift_jis').decode(arrayBuffer);
            data = JSON.parse(text) as Record<string, unknown>;
          } catch {
            setLoadError('サポートされていない文字エンコードまたはロープレファイルの形式が正しくありません。');
            return;
          }
        }
        const next: ScenarioFormData = { ...formData };
        if (data.title !== undefined) next.title = String(data.title);
        if (data.description !== undefined) next.description = String(data.description);
        if (data.goal !== undefined) next.goal = String(data.goal);
        if (data.estimatedtime !== undefined) next.estimatedTime = Number(data.estimatedtime) || 15;
        if (data.situation !== undefined) next.situation = String(data.situation);
        if (data.situationClosed !== undefined) next.situationClosed = String(data.situationClosed);
        if (data.showMaterialsOnOrientation !== undefined) next.showMaterialsOnOrientation = data.showMaterialsOnOrientation === true || data.showMaterialsOnOrientation === 'true';
        if (data.targetname !== undefined) next.targetName = String(data.targetname);
        if (data.targetgender !== undefined) {
          const v = String(data.targetgender);
          next.targetGender = genderOptions.includes(v) ? v : genderOptions[0];
        }
        if (data.targetage !== undefined) {
          const v = String(data.targetage);
          next.targetAge = ageOptions.includes(v) ? v : ageOptions[0];
        }
        if (data.targetlanguage !== undefined) {
          const v = String(data.targetlanguage);
          next.targetLanguage = languageOptions.includes(v) ? v : languageOptions[0];
        }
        if (data.department !== undefined && !currentUser?.department) next.department = String(data.department);
        if (data.tags !== undefined) {
          next.tags = Array.isArray(data.tags) ? (data.tags as string[]).join(', ') : String(data.tags);
        }
        setFormData(next);
      } catch {
        setLoadError('ファイルの読み込み中にエラーが発生しました。');
      }
    };
    input.click();
  };

  const handleContextMenu = (e: React.MouseEvent, material: Material) => {
    e.preventDefault();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, material });
  };

  const handleCloseContextMenu = () => setContextMenu(null);

  const handleViewTextFromContextMenu = async () => {
    if (!contextMenu?.material) {
      handleCloseContextMenu();
      return;
    }
    setTextDialogLoading(true);
    setTextDialogError(null);
    try {
      const { data } = await getMaterial({
        variables: { materialId: contextMenu.material.materialId }
      });
      if (data?.getMaterial) {
        setCurrentMaterial(data.getMaterial);
        setExtractedText(data.getMaterial.extractedText || '');
        setOpenTextDialog(true);
      }
    } catch (err) {
      console.error(err);
      setTextDialogError('資料の取得中にエラーが発生しました。');
    } finally {
      setTextDialogLoading(false);
    }
    handleCloseContextMenu();
  };

  const handleDeleteFromContextMenu = () => {
    if (contextMenu?.material) {
      setMaterialsToDelete(prev => [...prev, contextMenu.material!.materialId]);
    }
    handleCloseContextMenu();
  };

  const handleCloseTextDialog = () => {
    setOpenTextDialog(false);
    setCurrentMaterial(null);
    setExtractedText('');
    setTextDialogError(null);
  };

  const handleUpdateText = async () => {
    if (!currentMaterial) return;
    setTextDialogLoading(true);
    setTextDialogError(null);
    try {
      await updateMaterial({
        variables: {
          input: { materialId: currentMaterial.materialId, extractedText }
        }
      });
      handleCloseTextDialog();
    } catch (err) {
      console.error(err);
      setTextDialogError('テキストの更新中にエラーが発生しました。');
    } finally {
      setTextDialogLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle
          sx={{
            bgcolor: theme.palette.smartSkillTalk?.linkBlue || '#1976d2',
            color: 'white',
            fontSize: '18px',
            fontWeight: 400,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>{initialScenario ? 'ロープレ編集' : '新規ロープレ作成'}</span>
          <Button
            variant="outlined"
            onClick={handleLoadScenario}
            sx={{
              color: 'white',
              borderColor: 'white',
              borderRadius: '6px',
              fontSize: '14px',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            シナリオ読込
          </Button>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {dialogErrorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogErrorMessage(null)}>
              {dialogErrorMessage}
            </Alert>
          )}
          {loadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
              {loadError}
            </Alert>
          )}
          <TextField
            margin="dense"
            id="title"
            name="title"
            label={`ロープレのタイトルを付けてください（${SCENARIO_VALIDATIONS.TITLE.MAX_LENGTH}文字まで）`}
            type="text"
            fullWidth
            variant="outlined"
            value={formData.title}
            onChange={handleInputChange}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '3px', '& fieldset': { borderColor: '#898E93' } } }}
            required={SCENARIO_VALIDATIONS.TITLE.REQUIRED}
          />
          <TextField
            margin="dense"
            id="description"
            name="description"
            label={`受講者ユーザーに対してのロープレの概要などの説明を入力して下さい。（${SCENARIO_VALIDATIONS.DESCRIPTION.MAX_LENGTH}文字まで）`}
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={formData.description}
            onChange={handleInputChange}
            sx={{ mb: 2, '& .MuiInputBase-input': { resize: 'vertical', minHeight: '4em' } }}
            required={SCENARIO_VALIDATIONS.DESCRIPTION.REQUIRED}
          />
          <TextField
            margin="dense"
            id="goal"
            name="goal"
            label={`ロープレの目的を入力して下さい。（${SCENARIO_VALIDATIONS.GOAL.MAX_LENGTH}文字まで）`}
            multiline
            rows={1}
            fullWidth
            variant="outlined"
            value={formData.goal}
            onChange={handleInputChange}
            sx={{ mb: 2, '& .MuiInputBase-input': { resize: 'vertical', minHeight: '4em' } }}
            required={SCENARIO_VALIDATIONS.GOAL.REQUIRED}
          />
          <TextField
            margin="dense"
            id="estimatedTime"
            name="estimatedTime"
            label="目安時間（分） 最大30分までです。"
            type="number"
            fullWidth
            variant="outlined"
            value={formData.estimatedTime}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            InputProps={{ inputProps: { min: 1 } }}
            required={SCENARIO_VALIDATIONS.ESTIMATED_TIME.REQUIRED}
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              資料（PDFファイル）
            </Typography>
            {fileError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {fileError}
              </Alert>
            )}
            {initialScenario?.materials && initialScenario.materials.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  既存の添付資料: {initialScenario.materials.length}件
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {initialScenario.materials
                    .filter(m => !materialsToDelete.includes(m.materialId))
                    .map((material) => (
                      <Chip
                        key={material.materialId}
                        label={material.name}
                        onClick={async () => {
                          try {
                            const { presignedUrl } = await uploadService.getMaterialUrl(material.materialId);
                            window.open(presignedUrl, '_blank');
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                        onContextMenu={(e) => handleContextMenu(e, material)}
                        onDelete={() => setMaterialsToDelete(prev => [...prev, material.materialId])}
                        color={
                          material.extractionStatus === 'COMPLETED' ? 'primary' :
                          material.extractionStatus === 'PROCESSING' || material.extractionStatus === 'COMBINED' ? 'info' :
                          material.extractionStatus === 'FAILED' ? 'error' : 'primary'
                        }
                        variant="outlined"
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                </Box>
              </Box>
            )}
            <input
              accept="application/pdf"
              style={{ display: 'none' }}
              id="scenario-dialog-file-upload"
              multiple
              type="file"
              onChange={async (e) => {
                const files = e.target.files;
                if (!files?.length) return;
                const fileArray = Array.from(files);
                const errorMessage = validatePdfFiles(fileArray, uploadedFiles);
                if (errorMessage) {
                  setFileError(errorMessage);
                  return;
                }
                try {
                  for (const file of fileArray) {
                    const text = await extractTextFromPdf(file);
                    const textError = validatePdfTextLength(text, file.name);
                    if (textError) {
                      setFileError(textError);
                      return;
                    }
                  }
                  setFileError(null);
                  setUploadedFiles(prev => [...prev, ...fileArray]);
                  setFileNames(prev => [...prev, ...fileArray.map(f => f.name)]);
                } catch (err) {
                  console.error(err);
                  setFileError('PDFの検証中にエラーが発生しました。');
                }
                e.target.value = '';
              }}
            />
            <label htmlFor="scenario-dialog-file-upload">
              <Button variant="outlined" component="span" sx={{ mr: 2 }} disabled={uploadedFiles.length >= PDF_VALIDATIONS.MAX_FILES}>
                ファイルを選択
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary">
              PDFファイルのみ、1ファイル{PDF_VALIDATIONS.MAX_FILE_SIZE_MB()}MB以下、最大{PDF_VALIDATIONS.MAX_FILES}件まで
            </Typography>
            {fileNames.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" gutterBottom>選択されたファイル: {fileNames.length}件</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {fileNames.map((name, index) => (
                    <Chip
                      key={index}
                      label={name}
                      onDelete={() => {
                        const newFiles = uploadedFiles.filter((_, i) => i !== index);
                        const newNames = fileNames.filter((_, i) => i !== index);
                        setUploadedFiles(newFiles);
                        setFileNames(newNames);
                        setFileError(null);
                      }}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
            <FormControlLabel
              control={
                <Switch
                  name="showMaterialsOnOrientation"
                  checked={formData.showMaterialsOnOrientation}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label="資料をオリエンテーションで表示する"
              sx={{ mt: 1, display: 'block' }}
            />
          </Box>
          <TextField
            margin="dense"
            id="targetName"
            name="targetName"
            label={`相手の名前（${SCENARIO_VALIDATIONS.TARGET_NAME.MAX_LENGTH}文字まで）`}
            fullWidth
            variant="outlined"
            value={formData.targetName}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            required={SCENARIO_VALIDATIONS.TARGET_NAME.REQUIRED}
          />
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '80px' }}>相手の性別:</Typography>
            <RadioGroup
              row
              name="targetGender"
              value={formData.targetGender}
              onChange={(e) => setFormData(prev => ({ ...prev, targetGender: e.target.value }))}
              sx={{ display: 'flex', justifyContent: 'flex-start' }}
            >
              {genderOptions.map((g) => <FormControlLabel key={g} value={g} control={<Radio />} label={g} />)}
            </RadioGroup>
          </Box>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '80px' }}>相手の年代:</Typography>
            <RadioGroup
              row
              name="targetAge"
              value={formData.targetAge}
              onChange={(e) => setFormData(prev => ({ ...prev, targetAge: e.target.value }))}
              sx={{ display: 'flex', justifyContent: 'flex-start' }}
            >
              {ageOptions.map((a) => <FormControlLabel key={a} value={a} control={<Radio />} label={a} />)}
            </RadioGroup>
          </Box>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ mr: 2, minWidth: '80px' }}>相手の言語:</Typography>
            <RadioGroup
              row
              name="targetLanguage"
              value={formData.targetLanguage}
              onChange={(e) => setFormData(prev => ({ ...prev, targetLanguage: e.target.value }))}
              sx={{ display: 'flex', justifyContent: 'flex-start' }}
            >
              {languageOptions.map((l) => <FormControlLabel key={l} value={l} control={<Radio />} label={l} />)}
            </RadioGroup>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>状況の説明：</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              相手の課題や商談状況を説明して下さい。箇条書きで構いません。<br />
              リアリティのあるロープレをしたいのであれば、状況を細かく設定してください。
            </Typography>
            <Button variant="outlined" onClick={handleUseTemplate} sx={{ mb: 2 }}>
              テンプレートを使用して設定
            </Button>
            <TextField
              margin="dense"
              id="situation"
              name="situation"
              label={`状況設定（${SCENARIO_VALIDATIONS.SITUATION.MAX_LENGTH}文字まで）`}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={formData.situation}
              onChange={handleInputChange}
              sx={{ '& .MuiInputBase-input': { resize: 'vertical', minHeight: '3em' } }}
              required={SCENARIO_VALIDATIONS.SITUATION.REQUIRED}
            />
            <TextField
              margin="dense"
              id="situationClosed"
              name="situationClosed"
              label={`状況設定（クローズ）（${SCENARIO_VALIDATIONS.SITUATION_CLOSED.MAX_LENGTH}文字まで）`}
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={formData.situationClosed}
              onChange={handleInputChange}
              sx={{ mb: 2, '& .MuiInputBase-input': { resize: 'vertical', minHeight: '3em' } }}
              helperText="担当者には開示されません。ロープレのAI回答作成時に、上記の状況設定と同様に参照されます。"
              inputProps={{ maxLength: SCENARIO_VALIDATIONS.SITUATION_CLOSED.MAX_LENGTH }}
            />
          </Box>
          <TextField
            margin="dense"
            id="department"
            name="department"
            label={`利用できる所属（${SCENARIO_VALIDATIONS.DEPARTMENT.MAX_LENGTH}文字まで）`}
            fullWidth
            variant="outlined"
            value={formData.department || ''}
            onChange={handleInputChange}
            required={SCENARIO_VALIDATIONS.DEPARTMENT.REQUIRED}
            disabled={!!currentUser?.managementScope?.trim() || !!currentUser?.department}
            sx={{ mb: 2 }}
            helperText="未入力の場合、全所属で利用可能となります"
          />
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={formData.tags.split(',').map(t => t.trim()).filter(Boolean)}
            onChange={(_, newValue) => {
              const cleaned = Array.from(
                new Set(newValue.map(v => String(v).trim()).filter(Boolean))
              );
              setFormData(prev => ({ ...prev, tags: cleaned.join(', ') }));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={option} {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="タグ（複数可）"
                placeholder="タグを入力"
                helperText="例: 営業スキル, 初級, 電話対応"
                sx={{ mb: 2 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => handleSave(true)}
              variant="contained"
              disabled={saving || isSaveDisabled()}
              sx={{ bgcolor: '#5EB600', color: 'white', borderRadius: '6px', '&:hover': { bgcolor: '#5EB600', opacity: 0.9 } }}
            >
              {saving ? <CircularProgress size={24} /> : 'ロープレ作成（公開する）'}
            </Button>
            <Button
              onClick={() => handleSave(false)}
              variant="outlined"
              disabled={saving || isSaveDisabled()}
              sx={{ color: '#3a4753ff', borderColor: '#5D84AF', borderRadius: '6px', '&:hover': { borderColor: '#5D84AF', opacity: 0.9 } }}
            >
              {saving ? <CircularProgress size={24} /> : '一時保存（非公開）'}
            </Button>
          </Box>
          <Button onClick={onClose} sx={{ color: '#666666', borderColor: '#666666', borderRadius: '6px', '&:hover': { borderColor: '#666666', opacity: 0.9 } }} variant="outlined">
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      {/* テンプレート選択ダイアログ */}
      <Dialog
        open={openTemplateDialog}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCloseTemplateDialog();
        }}
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', color: 'white', fontSize: '18px', fontWeight: 400 }}>
          テンプレートを選択
        </DialogTitle>
        <DialogContent>
          {templateLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : (
            <RadioGroup value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
              {templateData?.listSituationTemplate?.items?.length ? (
                [...templateData.listSituationTemplate.items]
                  .sort((a: SituationTemplate, b: SituationTemplate) => a.title.localeCompare(b.title))
                  .map((t: SituationTemplate) => (
                    <FormControlLabel key={t.templateId} value={t.templateId} control={<Radio />} label={t.title} sx={{ mb: 1 }} />
                  ))
              ) : (
                <Typography>利用可能なテンプレートがありません</Typography>
              )}
            </RadioGroup>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleApplyTemplate} variant="contained" disabled={!selectedTemplateId} sx={{ bgcolor: '#5EB600', color: 'white', borderRadius: '6px', '&:hover': { bgcolor: '#5EB600', opacity: 0.9 } }}>
            設定
          </Button>
          <Button onClick={handleCloseTemplateDialog} sx={{ color: '#666666', borderColor: '#666666', borderRadius: '6px' }} variant="outlined">
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MenuItem onClick={handleViewTextFromContextMenu} disabled={textDialogLoading}>
          {textDialogLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          テキストの確認
        </MenuItem>
        <MenuItem onClick={handleDeleteFromContextMenu}>削除</MenuItem>
      </Menu>

      <Dialog
        open={openTextDialog}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') handleCloseTextDialog();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ bgcolor: theme.palette.smartSkillTalk?.linkBlue || '#1976d2', color: 'white', fontSize: '18px', fontWeight: 400 }}>
          添付資料のテキスト確認・編集
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {textDialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTextDialogError(null)}>{textDialogError}</Alert>
          )}
          <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>ファイル名: {currentMaterial?.name}</Typography>
          <TextField
            fullWidth
            multiline
            rows={15}
            variant="outlined"
            value={extractedText}
            onChange={(e) => setExtractedText(e.target.value)}
            placeholder="抽出されたテキストがここに表示されます"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontFamily: 'monospace' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleUpdateText} variant="contained" disabled={textDialogLoading} sx={{ bgcolor: '#5EB600', color: 'white', borderRadius: '6px', '&:hover': { bgcolor: '#5EB600', opacity: 0.9 } }}>
            {textDialogLoading ? <CircularProgress size={24} /> : '更新'}
          </Button>
          <Button onClick={handleCloseTextDialog} variant="outlined" disabled={textDialogLoading} sx={{ color: '#666666', borderColor: '#666666', borderRadius: '6px' }}>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScenarioFormDialog;
